/**
 * TURN CC · 22 → CC · 24 (perf refresh).
 *
 * Calendar events hook — aligned on the platform-wide instant-hydration
 * pattern (see queryClient.js + queryPersister.js + RestoreGate).
 *
 * Cache strategy:
 *  - staleTime 60s     : silent revalidation after 1 min.
 *  - gcTime 24h        : matches the persister maxAge so a full reload
 *                        hydrates from localStorage instantly (no flash).
 *  - placeholderData   : keepPreviousData → month navigation shows the
 *                        previous month's events until the new window
 *                        resolves (zero blank state).
 *  - refetchOnMount=false / refetchOnWindowFocus=false : no bounce on
 *                        remounts; the shared 5-min refetchInterval keeps
 *                        the data fresh in the background.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { API, authHeaders, apiJson } from "@/lib/api";

const ALL_TYPES = ["daily_brief", "weekly_brief", "monthly_brief",
                   "weekly_quotes", "corporate_event"];

// Exposed so the AppShell prefetcher can reuse the same queryFn.
export function calendarEventsQueryFn({ from, to, types }) {
  const params = new URLSearchParams({ from, to });
  if (Array.isArray(types) && types.length > 0
      && types.length < ALL_TYPES.length) {
    params.set("types", types.join(","));
  }
  return apiJson(`${API}/calendar/events?${params.toString()}`, {
    headers: authHeaders(),
  });
}

export function calendarEventsQueryKey({ from, to, types }) {
  // Normalise the `types` slot so "all types" collapses to a single
  // stable key ("all") whether callers pass `undefined`, `[]`, or the
  // full `ALL_TYPES` array. Any subset uses a sorted CSV.
  const isAll = !Array.isArray(types) || types.length === 0
             || types.length >= ALL_TYPES.length;
  const t = isAll ? "all" : [...types].sort().join(",");
  return ["calendar", "events", from, to, t];
}

export function useCalendarEvents({ from, to, types } = {}) {
  return useQuery({
    queryKey: calendarEventsQueryKey({ from, to, types }),
    enabled: Boolean(from && to),
    queryFn: () => calendarEventsQueryFn({ from, to, types }),
    staleTime: 60_000,                    // 1 min fresh
    gcTime: 24 * 60 * 60_000,             // 24h — matches persister maxAge
    placeholderData: keepPreviousData,    // no flash on month change
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60_000,          // silent revalidation
  });
}

export const CALENDAR_ALL_TYPES = ALL_TYPES;
